"use client";

import { useRef, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Info, ChevronRight, ChevronLeft } from "lucide-react";
import { animate } from "animejs";
import { Link } from "@/i18n/navigation";
import { isRTL, type Locale } from "@/i18n/routing";
import { useNavigation } from "@/contexts/navigation-context";

export function QuizInstructionsClient() {
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const { navigateTo } = useNavigation();
  const instructionsRef = useRef<HTMLDivElement>(null);
  const rtl = isRTL(locale);

  useEffect(() => {
    if (instructionsRef.current) {
      animate(instructionsRef.current, {
        opacity: [0, 1],
        translateY: [20, 0],
        ease: "out(3)",
        duration: 600,
      });
    }
  }, []);

  const startQuiz = () => {
    if (instructionsRef.current) {
      animate(instructionsRef.current, {
        opacity: 0,
        translateY: -20,
        ease: "in(2)",
        duration: 300,
        onComplete: () => {
          navigateTo("/quiz");
        },
      });
    } else {
      navigateTo("/quiz");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-4xl px-4 py-8">
        <div
          className="w-full max-w-2xl mx-auto px-4 py-8 flex flex-col min-h-[70vh] items-center justify-center"
          data-testid="quiz-instructions-component"
        >
          <div
            ref={instructionsRef}
            className="w-full bg-card border border-border shadow-lg rounded-2xl p-6 md:p-8 flex flex-col gap-6"
          >
            <div className="flex items-center gap-3 text-primary mb-2">
              <Info className="w-8 h-8" />
              <h1 className="text-2xl font-bold">{t("instructions_title")}</h1>
            </div>

            <div className="space-y-4 text-foreground/90 leading-relaxed">
              <div className="flex gap-3">
                <span className="font-bold text-primary">1.</span>
                <p>{t("instructions_1")}</p>
              </div>
              <div className="flex gap-3">
                <span className="font-bold text-primary">2.</span>
                <p>{t("instructions_2")}</p>
              </div>
              <div className="flex gap-3">
                <span className="font-bold text-primary">3.</span>
                <p>{t("instructions_3")}</p>
              </div>
              <div className="flex gap-3">
                <span className="font-bold text-primary">4.</span>
                <p>{t("instructions_4")}</p>
              </div>
            </div>

            <div className="pt-4 border-t border-border mt-2">
              <button
                onClick={startQuiz}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 rounded-xl font-bold text-lg transition-all shadow-md hover:shadow-lg transform active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
                data-testid="quiz-instructions-start"
              >
                {t("start_btn")}{" "}
                <ChevronRight className={rtl ? "rotate-180" : ""} />
              </button>
            </div>
          </div>

          <Link
            href="/"
            className="mt-6 text-muted-foreground hover:text-foreground text-sm font-medium transition-colors flex items-center gap-2"
            data-testid="quiz-instructions-back"
          >
            <ChevronLeft className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />{" "}
            {t("btn_back")}
          </Link>
        </div>
      </main>
    </div>
  );
}
